import type { ItemView, WeekDay } from '@fall26/shared'
import { ItemRow } from '../ItemRow.js'

/**
 * One day's contents: what meets, and what is due.
 *
 * A meeting that does NOT run is still shown, struck through with the reason.
 * Two of the thirteen weekly blocks are conditional labs, and silently omitting
 * them would leave me unable to tell "no lab this week" from "I forgot".
 */
export function DayPanel({
  day,
  hue,
  onToggle,
  onOpen,
}: {
  day: WeekDay
  hue: (courseId: string) => number
  onToggle: (item: ItemView, done: boolean) => void
  /** Opens the editor for a row. */
  onOpen: (item: ItemView) => void
}) {
  const nothing = day.meetings.length === 0 && day.due.length === 0

  return (
    <div className="space-y-2">
      {day.noClassReason && (
        <div className="tabular rounded-[var(--radius-sm)] bg-[var(--day-break)] px-2 py-1 text-[11px] text-[var(--text-muted)]">
          {day.noClassReason}
        </div>
      )}
      {day.milestone && (
        <div className="tabular rounded-[var(--radius-sm)] border border-[var(--accent-line)] bg-[var(--accent-wash)] px-2 py-1 text-[11px] text-[var(--accent)]">
          {day.milestone}
        </div>
      )}

      {day.meetings.map((meeting) => (
        <div
          key={`${meeting.courseId}-${meeting.start}`}
          className="flex items-baseline gap-2 border-l-2 pl-2"
          style={{ borderColor: meeting.meets ? `var(--course-${meeting.colorIndex % 6})` : 'var(--border)' }}
        >
          <span
            className={`tabular text-[11px] ${
              meeting.meets ? 'text-[var(--text-muted)]' : 'text-[var(--text-subtle)] line-through'
            }`}
          >
            {meeting.start}
          </span>
          <span className="min-w-0 flex-1">
            <span
              className={`text-[12px] ${
                meeting.meets ? 'text-[var(--text)]' : 'text-[var(--text-subtle)] line-through'
              }`}
            >
              {meeting.sessionTitle ?? meeting.label}
            </span>
            {meeting.meets && meeting.room && (
              <span className="tabular ml-1.5 text-[11px] text-[var(--text-subtle)]">{meeting.room}</span>
            )}
            {!meeting.meets && meeting.skippedReason && (
              <span className="tabular ml-1.5 text-[11px] text-[var(--text-subtle)]">
                — {meeting.skippedReason}
              </span>
            )}
          </span>
        </div>
      ))}

      {day.due.length > 0 && (
        <ul className="space-y-1.5 pt-0.5">
          {day.due.map((item) => (
            <ItemRow
              key={item.id}
              item={item}
              dense
              colorIndex={hue(item.courseId)}
              onToggle={(done) => onToggle(item, done)}
              onOpen={() => onOpen(item)}
            />
          ))}
        </ul>
      )}

      {nothing && <p className="text-[12px] text-[var(--text-subtle)]">Nothing.</p>}
    </div>
  )
}
